import { useEffect, useRef, useState, type ReactNode } from 'react';
import { searchNodes, SEARCH_LIMIT, type Hit } from './node-search.js';
import type { TreeIndex } from './node-tree-path.js';

const GLASS = (
  <svg width="15" height="15" viewBox="0 0 15 15" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <circle cx="6.6" cy="6.6" r="4.4" />
      <path d="M10 10 L13.2 13.2" />
    </g>
  </svg>
);

/**
 * 搜索：一个从顶上落下来的面板，输入框在最上面，命中列在下面。
 *
 * 不居中：这一类面板要能一直待在视线落点上——人敲字的时候眼睛在输入框，
 * 结果从它底下长出来。居中的话，结果多起来时输入框会跟着往上跑。
 *
 * 键盘走得通是这件事的一半：↑↓ 选，Enter 进去，Esc 关掉。用 ⌘K / Ctrl+K 打开。
 * 只能用鼠标点的搜索框，对每天要找几十次标准的人来说等于没有。
 */
export function SearchDialog({
  index,
  rootName,
  organizationCode,
  onPick,
  onClose,
}: {
  index: TreeIndex;
  rootName: string;
  organizationCode: string;
  /** 选中一条。外层负责跳过去并关掉这个面板。 */
  onPick: (id: string) => void;
  onClose: () => void;
}): ReactNode {
  const [query, setQuery] = useState('');
  /** 键盘选中的第几条。每次改查询都归零——重排之后原来那个位置指的已经不是同一条了。 */
  const [at, setAt] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const { hits, total } = searchNodes(index, rootName, query, organizationCode);
  const active = Math.min(at, Math.max(0, hits.length - 1));

  /*
   * Esc 关掉。不用 useDismissOnOutside：那个钩子在 pointerdown 时判「点在框外」，
   * 而这一层有自己的遮罩，点遮罩本身就是点在框外——由遮罩的 onClick 处理更直接。
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /** 键盘选中的那条滚进视野。只滚必要的距离，不把它拽到正中间。 */
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function onKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setAt(Math.min(active + 1, hits.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setAt(Math.max(active - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const hit = hits[active];
      if (hit !== undefined) onPick(hit.id);
    }
  }

  return (
    <div
      className="backdrop search-back"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="search-panel" role="dialog" aria-modal="true" aria-label="搜索">
        <div className="search-field">
          <span className="search-glass" aria-hidden="true">
            {GLASS}
          </span>
          <input
            // 打开就能敲，不用先点一下输入框
            autoFocus
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setAt(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="搜组织和 ATC，名字或编码"
            aria-label="搜索"
            role="combobox"
            aria-expanded={hits.length > 0}
            aria-controls="search-hits"
          />
          <kbd className="search-esc">Esc</kbd>
        </div>

        {query.trim() === '' ? (
          <p className="search-hint">
            输入名字或编码。编码是印在二维码标签上那一串，比如 <code>ATC-EQU-001</code>。
          </p>
        ) : hits.length === 0 ? (
          <p className="search-hint">没有匹配的组织或 ATC。</p>
        ) : (
          <div className="search-hits" id="search-hits" role="listbox" ref={listRef}>
            {hits.map((hit, slot) => (
              <HitRow
                key={hit.id}
                hit={hit}
                selected={slot === active}
                onEnter={() => setAt(slot)}
                onPick={() => onPick(hit.id)}
              />
            ))}

            {/*
              截断了就说出来。默默只给前 30 条，会让人以为「一共就这么多」，
              而那正好是搜索最不该撒谎的地方。
            */}
            {total > hits.length && (
              <p className="search-more">
                命中 {total} 条，先列前 {SEARCH_LIMIT} 条。再敲几个字缩小范围。
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function HitRow({
  hit,
  selected,
  onEnter,
  onPick,
}: {
  hit: Hit;
  selected: boolean;
  onEnter: () => void;
  onPick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className="search-hit"
      role="option"
      aria-selected={selected}
      // 鼠标滑过就把键盘选中挪过来，两套选中只有一个，不会出现「亮着两行」
      onMouseEnter={onEnter}
      onClick={onPick}
    >
      <span className="sh-main">
        {hit.typeName !== null && <span className="node-type">{hit.typeName}</span>}
        <span className="sh-name">{hit.name}</span>
      </span>

      <span className="sh-side">
        {/* 路径：两个同名的「甲班」只能靠它分开。 */}
        {hit.path.length > 0 && <span className="sh-path">{hit.path.join(' / ')}</span>}
        {hit.code !== null && <span className="sh-code">{hit.code}</span>}
      </span>
    </button>
  );
}
